import React, { useCallback } from 'react'
import { Platform } from 'react-native'

import { useToast } from '../../_gluestack/toast'
import { Notification, type NotificationStatus } from './Notification'

export interface NotifyOptions {
    status?: NotificationStatus
    message: string
    /** Optional inline action (e.g. "Go to issue"). */
    actionLabel?: string
    onAction?: () => void
    /** Auto-dismiss after N ms. Pass `null` to keep it until dismissed. Default 5000. */
    duration?: number | null
    showIcon?: boolean
    /** Show the bottom countdown bar. Defaults to true when auto-dismissing. */
    showTimeoutBar?: boolean
}

const DEFAULT_DURATION = 5000

/**
 * Returns a `notify()` that renders the universal {@link Notification} through
 * the gluestack toast system (cross-platform web + mobile). For non-React
 * callers (sagas, services) use the module-level `notify` from
 * `notificationController` instead — it proxies to a mounted `<NotificationHost/>`.
 */
export function useNotify() {
    const toast = useToast()

    return useCallback(
        (opts: NotifyOptions) => {
            const duration = opts.duration === undefined ? DEFAULT_DURATION : opts.duration
            return toast.show({
                duration: duration ?? undefined,
                // ponytail: web toast was top-center pre-AOS-822 (react-toastify); restore it. Mobile keeps its
                // original bottom placement (react-native-toast-message default), so this stays web-only.
                placement: Platform.OS === 'web' ? 'top' : undefined,
                render: ({ id }: { id: string }) => (
                    <Notification
                        status={opts.status}
                        actionLabel={opts.actionLabel}
                        onActionPress={opts.onAction}
                        onClose={() => toast.close(id)}
                        showIcon={opts.showIcon}
                        showTimeoutBar={opts.showTimeoutBar ?? duration !== null}
                        duration={duration}
                    >
                        {opts.message}
                    </Notification>
                ),
            })
        },
        [toast],
    )
}

export type NotifyFn = ReturnType<typeof useNotify>
